import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import CharacterHUD from "@/components/CharacterHUD";
import Mythodex from "@/components/Mythodex";
import DiceRollOverlay from "@/components/DiceRollOverlay";
import PaywallModal from "@/components/PaywallModal";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChevronLeft, BookOpen, Undo2, X } from "lucide-react";
import { diffMythodex } from "@/lib/mythodexDiff";
import { useSwipe } from "@/lib/useSwipe";

const STREAM_STORAGE_KEY = "mm.streamed_turns.v1";
const FREE_TURN_LIMIT = 10;
const CHAPTER_LENGTH = 15;
const CHAPTER_WARNING_FROM = 11;

function loadStreamed() {
  try {
    return new Set(JSON.parse(localStorage.getItem(STREAM_STORAGE_KEY) || "[]"));
  } catch {
    return new Set();
  }
}

function markStreamed(id) {
  const set = loadStreamed();
  set.add(id);
  // cap to last 500 to avoid unbounded growth
  const arr = Array.from(set).slice(-500);
  localStorage.setItem(STREAM_STORAGE_KEY, JSON.stringify(arr));
}

// iOS Safari overlays the keyboard instead of resizing dvh; track the visual viewport so the input bar stays above it.
function useVisualViewportHeight() {
  const [h, setH] = useState(null);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const onResize = () => {
      setH(vv.height);
      window.scrollTo(0, 0);
    };
    vv.addEventListener("resize", onResize);
    return () => vv.removeEventListener("resize", onResize);
  }, []);
  return h;
}

export default function Play() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [adventure, setAdventure] = useState(null);
  const [turns, setTurns] = useState([]);
  const [action, setAction] = useState("");
  const [busy, setBusy] = useState(false);
  const [dice, setDice] = useState(null);
  const [hudOpen, setHudOpen] = useState(false);
  const [paywall, setPaywall] = useState(null);
  const [streaming, setStreaming] = useState(false);  // is any turn currently animating
  const [alreadyStreamed, setAlreadyStreamed] = useState(loadStreamed);
  const [sidebarTab, setSidebarTab] = useState("sheet");  // "sheet" | "mythodex"
  const [freshKeys, setFreshKeys] = useState(() => new Set());
  const [tabPulse, setTabPulse] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const viewportHeight = useVisualViewportHeight();
  const feedRef = useRef(null);
  const [drawerDx, setDrawerDx] = useState(0);
  const edgeSwipe = useSwipe({ onLeft: () => setHudOpen(true) });
  const drawerSwipe = useSwipe({ onRight: () => setHudOpen(false), onDrag: (dx) => setDrawerDx(Math.max(0, dx)) });
  const onPageTouchStart = (e) => {
    const x = e.touches[0].clientX;
    if (!hudOpen && window.innerWidth < 1024 && x > window.innerWidth - 32) edgeSwipe.onTouchStart(e);
  };

  const onInputFocus = () => {
    setInputFocused(true);
    // keep the latest narrative visible once the phone keyboard has resized the viewport
    setTimeout(() => feedRef.current?.scrollTo({ top: feedRef.current.scrollHeight, behavior: "smooth" }), 300);
  };

  // Once the Mythodex is actually on screen, stop the tab pulse and let entry glows fade.
  useEffect(() => {
    if (freshKeys.size === 0 || sidebarTab !== "mythodex") return;
    if (!hudOpen && !window.matchMedia("(min-width: 1024px)").matches) return;
    setTabPulse(false);
    const t = setTimeout(() => setFreshKeys(new Set()), 5000);
    return () => clearTimeout(t);
  }, [freshKeys, sidebarTab, hudOpen]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/adventures/${id}`);
        setAdventure(data.adventure);
        setTurns(data.turns);
      } catch (e) {
        toast.error("Adventure not found");
        navigate("/app");
      }
    })();
  }, [id, navigate]);

  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTo({ top: feedRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [turns.length]);

  const onTurnRevealed = useCallback((turnId) => {
    markStreamed(turnId);
    setAlreadyStreamed((prev) => {
      const next = new Set(prev);
      next.add(turnId);
      return next;
    });
    setStreaming(false);
    // ease into scroll to bottom as text reveals
    if (feedRef.current) {
      feedRef.current.scrollTo({ top: feedRef.current.scrollHeight, behavior: "smooth" });
    }
  }, []);

  const submitAction = async (customAction) => {
    const text = (customAction ?? action).trim();
    if (!text || busy || adventure.is_locked || streaming) return;
    setBusy(true);
    try {
      const { data } = await api.post("/adventures/action", { adventure_id: id, action: text });
      const changed = diffMythodex(adventure.mythodex, data.adventure.mythodex);
      if (changed.size > 0) {
        setFreshKeys(changed);
        setTabPulse(true);
      }
      if (data.recap_turn) toast(`Chapter ${data.recap_turn.chapter_opened} begins`);
      setAdventure(data.adventure);
      const fresh = await api.get(`/adventures/${id}`);
      setTurns(fresh.data.turns);
      setAction("");
      setStreaming(true);  // the newly-appended GM turn will animate
      if (data.gm_turn.dice_roll) {
        // hold dice overlay until narrative finishes revealing feels heavy; show first
        setDice(data.gm_turn.dice_roll);
      }
      if (data.gm_turn.encounter) toast.warning("A random encounter unfolds. It cannot be avoided.");
      if (data.gm_turn.death) toast.error("The story ends here. Chronicle locked.");
    } catch (e) {
      if (e.response?.status === 402) {
        setPaywall({ reason: "turn_limit", message: e.response.data?.detail });
      } else {
        toast.error(e.response?.data?.detail || "Action failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    if (!adventure) return;
    if (adventure.hard_mode && adventure.undos_remaining <= 0) {
      toast.error("No undos remaining in Hard Mode.");
      return;
    }
    try {
      await api.post("/adventures/undo", { adventure_id: id });
      const fresh = await api.get(`/adventures/${id}`);
      setAdventure(fresh.data.adventure);
      setTurns(fresh.data.turns);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Undo failed");
    }
  };

  if (!adventure) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-mono text-xs uppercase tracking-widest text-stone-500">Loading the tome...</div>
      </div>
    );
  }

  const lastGm = [...turns].reverse().find((t) => t.role === "gm");
  const choicesReady = lastGm && !streaming && alreadyStreamed.has(lastGm.turn_id);

  return (
    <div className="h-[100dvh] relative z-10 flex flex-col overflow-hidden" style={viewportHeight ? { height: viewportHeight } : undefined}
      onTouchStart={onPageTouchStart}
      onTouchEnd={edgeSwipe.onTouchEnd}
      data-testid="play-page"
    >
      {!hudOpen && (
        <div className="lg:hidden fixed right-0 top-1/2 -translate-y-1/2 w-1 h-16 rounded-l-sm bg-amber-500/40 z-20 pointer-events-none" data-testid="drawer-edge-handle" aria-hidden="true" />
      )}
      <header className="border-b border-amber-500/20 backdrop-blur-md bg-stone-950/70 z-20 shrink-0 safe-top">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-2.5 sm:py-3 flex items-center gap-2 sm:gap-3 justify-between">
          <button data-testid="back-to-vault" onClick={() => navigate("/app")} aria-label="Back to Vault" className="btn-ghost text-[0.65rem] min-h-[44px] min-w-[44px] flex items-center justify-center gap-1">
            <ChevronLeft size={16} /> <span className="hidden sm:inline">Vault</span>
          </button>
          <div className="flex-1 min-w-0 text-center">
            <div className="font-serif-display text-sm sm:text-base gold line-clamp-2 sm:truncate leading-tight" data-testid="play-title">{adventure.title}</div>
            <div className="font-mono text-[0.6rem] uppercase tracking-widest text-stone-500" data-testid="turn-counter">
              {user && !user.premium
                ? `Turn ${adventure.turn_count} / ${FREE_TURN_LIMIT} · Free preview`
                : `Turn ${adventure.turn_count} · ${adventure.hard_mode ? `Hard · ${adventure.undos_remaining} undo${adventure.undos_remaining === 1 ? "" : "s"}` : "Casual"}`}
            </div>
          </div>
          <button data-testid="toggle-hud" onClick={() => setHudOpen(!hudOpen)} aria-label="Open Sheet and Mythodex" className="btn-ghost text-[0.65rem] lg:hidden min-h-[44px] min-w-[44px] flex items-center justify-center gap-1.5 relative">
            <BookOpen size={16} /> <span className="hidden sm:inline">Sheet / Mythodex</span>
            {tabPulse && <span className="tab-dot absolute -top-1 -right-1 !ml-0" data-testid="toggle-hud-pulse-dot" />}
          </button>
        </div>
        {adventure.is_locked && (
          <div className="bg-purple-950/40 border-t border-purple-700/40 text-center py-2 font-mono text-[0.7rem] uppercase tracking-widest text-purple-300" data-testid="permadeath-banner">
            ◆ Permadeath · This chronicle is sealed forever
          </div>
        )}
        {!adventure.is_locked && (() => {
          const tc = adventure.turn_count || 0;
          if (tc <= 0) return null;
          const turnInChapter = ((tc - 1) % CHAPTER_LENGTH) + 1;
          if (turnInChapter < CHAPTER_WARNING_FROM) return null;
          const left = CHAPTER_LENGTH - turnInChapter;
          const nextChapter = (adventure.chapter || 1) + 1;
          const label = left === 0
            ? `◆ Chapter ${adventure.chapter} ends · Chapter ${nextChapter} begins next turn`
            : `◆ Chapter ${adventure.chapter} wanes · ${left} turn${left === 1 ? "" : "s"} until Chapter ${nextChapter}`;
          return (
            <div
              className="bg-amber-950/30 border-t border-amber-500/30 text-center py-2 font-mono text-[0.7rem] uppercase tracking-widest gold"
              data-testid="chapter-warning-banner"
            >
              {label}
            </div>
          );
        })()}
      </header>

      <div className="flex-1 min-h-0 flex max-w-6xl mx-auto w-full">
        <main ref={feedRef} className="flex-1 overflow-y-auto overscroll-contain px-4 sm:px-8 py-6 sm:py-8" data-testid="story-feed">
          <div className="max-w-2xl mx-auto space-y-8">
            {turns.map((t, i) => (
              <TurnBlock
                key={t.turn_id}
                turn={t}
                isFirst={i === 0}
                animate={t.role === "gm" && !alreadyStreamed.has(t.turn_id)}
                onRevealed={onTurnRevealed}
              />
            ))}
            {busy && (
              <div className="fade-up font-mono text-xs uppercase tracking-widest gold animate-pulse">The tome writes...</div>
            )}
          </div>
        </main>

        <aside className="hidden lg:flex lg:flex-col w-80 border-l border-amber-500/20 bg-stone-950/60">
          <div className="flex border-b border-amber-500/20 shrink-0">
            <button
              data-testid="sidebar-tab-sheet"
              onClick={() => setSidebarTab("sheet")}
              className={`flex-1 py-2.5 font-mono text-[0.65rem] uppercase tracking-[0.35em] transition-colors ${
                sidebarTab === "sheet"
                  ? "gold border-b-2 border-amber-500 bg-amber-500/5"
                  : "text-stone-500 hover:text-amber-400"
              }`}
            >
              Sheet
            </button>
            <button
              data-testid="sidebar-tab-mythodex"
              onClick={() => setSidebarTab("mythodex")}
              data-pulse={tabPulse && sidebarTab !== "mythodex" ? "true" : undefined}
              className={`flex-1 py-2.5 font-mono text-[0.65rem] uppercase tracking-[0.35em] transition-colors ${
                sidebarTab === "mythodex"
                  ? "gold border-b-2 border-amber-500 bg-amber-500/5"
                  : `text-stone-500 hover:text-amber-400 ${tabPulse ? "tab-pulse" : ""}`
              }`}
            >
              Mythodex{tabPulse && sidebarTab !== "mythodex" && <span className="tab-dot" />}
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {sidebarTab === "sheet" ? (
              <CharacterHUD adventure={adventure} />
            ) : (
              <Mythodex adventure={adventure} fresh={freshKeys} />
            )}
          </div>
        </aside>
      </div>

      {!adventure.is_locked && (
        <div className="border-t border-amber-500/20 backdrop-blur-md bg-stone-950/90 z-10 shrink-0 safe-bottom">
          <div className="max-w-3xl mx-auto px-3 sm:px-6 pt-3 space-y-2.5 sm:space-y-3">
            {user && !user.premium && adventure.turn_count >= FREE_TURN_LIMIT ? (
              <div className="rounded-sm border border-amber-500/40 bg-amber-500/10 p-4 flex items-center justify-between gap-4 flex-wrap" data-testid="free-preview-ended">
                <div>
                  <div className="font-mono text-[0.6rem] uppercase tracking-[0.35em] gold mb-1">Free Preview Ended</div>
                  <div className="font-serif-body text-stone-200 text-sm">You have reached the 10-turn preview. Ascend to Sovereign to continue this chronicle and play every world without limit.</div>
                </div>
                <button
                  data-testid="ascend-from-play-btn"
                  onClick={() => setPaywall({ reason: "turn_limit" })}
                  className="btn-gold"
                >
                  Ascend
                </button>
              </div>
            ) : (
              <>
                {choicesReady && lastGm?.choices?.length > 0 && (
                  <div
                    className={`flex-col sm:flex-row sm:flex-wrap gap-2 fade-up max-h-[40dvh] overflow-y-auto overscroll-contain ${inputFocused ? "hidden sm:flex" : "flex"}`}
                    data-testid="choices-row"
                  >
                    {lastGm.choices.map((c, i) => (
                      <button
                        key={`${lastGm.turn_id}-choice-${i}`}
                        data-testid={`player-choice-button-${i}`}
                        disabled={busy}
                        onClick={() => submitAction(c)}
                        className="text-left px-4 py-3 min-h-[48px] rounded-sm border border-amber-500/30 hover:border-amber-500 hover:bg-amber-500/10 active:bg-amber-500/20 active:border-amber-500 disabled:opacity-40 font-serif-body text-[0.95rem] sm:text-sm leading-snug transition-colors sm:flex-1 sm:min-w-[180px] flex items-start gap-2.5"
                      >
                        <span className="font-mono text-[0.65rem] gold mt-0.5 shrink-0">{i + 1}</span>
                        <span>{c}</span>
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 items-stretch">
                  <input
                    data-testid="action-input"
                    value={action}
                    onChange={(e) => setAction(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && submitAction()}
                    onFocus={onInputFocus}
                    onBlur={() => setInputFocused(false)}
                    enterKeyHint="send"
                    autoComplete="off"
                    placeholder={streaming ? "The GM is speaking..." : "Or write your own action..."}
                    disabled={busy || streaming}
                    className="flex-1 min-w-0 min-h-[48px] bg-stone-900/80 border border-amber-500/25 focus:border-amber-500 outline-none px-3 py-2 rounded-sm font-prose text-base text-stone-200 placeholder:text-stone-600 disabled:opacity-60"
                  />
                  <button data-testid="submit-action" onMouseDown={(e) => e.preventDefault()} onClick={() => submitAction()} disabled={busy || streaming || !action.trim()} className="btn-gold min-h-[48px]">
                    Act
                  </button>
                  <button data-testid="undo-btn" onClick={undo} disabled={busy || streaming || (adventure.hard_mode && adventure.undos_remaining <= 0)} className="btn-ghost min-h-[48px] min-w-[48px] flex items-center justify-center disabled:opacity-40" title="Undo last exchange" aria-label="Undo last exchange">
                    <Undo2 size={18} />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {dice && <DiceRollOverlay result={dice} onDone={() => setDice(null)} />}
      {hudOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-stone-950/80 backdrop-blur-sm"
          onClick={() => setHudOpen(false)}
          data-testid="hud-drawer-overlay"
        >
          <div
            className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-stone-950 border-l border-amber-500/30 flex flex-col drawer-slide-in"
            style={{ transform: `translateX(${drawerDx}px)`, transition: drawerDx === 0 ? "transform 200ms ease-out" : "none" }}
            onClick={(e) => e.stopPropagation()}
            {...drawerSwipe}
            data-testid="hud-drawer-panel"
          >
            <div className="flex border-b border-amber-500/20 shrink-0">
              <button
                data-testid="mobile-tab-sheet"
                onClick={() => setSidebarTab("sheet")}
                className={`flex-1 py-3 min-h-[48px] font-mono text-[0.65rem] uppercase tracking-[0.35em] transition-colors ${
                  sidebarTab === "sheet" ? "gold border-b-2 border-amber-500" : "text-stone-500"
                }`}
              >
                Sheet
              </button>
              <button
                data-testid="mobile-tab-mythodex"
                onClick={() => setSidebarTab("mythodex")}
                className={`flex-1 py-3 min-h-[48px] font-mono text-[0.65rem] uppercase tracking-[0.35em] transition-colors ${
                  sidebarTab === "mythodex" ? "gold border-b-2 border-amber-500" : `text-stone-500 ${tabPulse ? "tab-pulse" : ""}`
                }`}
              >
                Mythodex{tabPulse && sidebarTab !== "mythodex" && <span className="tab-dot" />}
              </button>
              <button
                data-testid="mobile-drawer-close"
                onClick={() => setHudOpen(false)}
                aria-label="Close"
                className="w-12 flex items-center justify-center text-stone-400 hover:text-amber-400"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {sidebarTab === "sheet" ? (
                <CharacterHUD adventure={adventure} />
              ) : (
                <Mythodex adventure={adventure} fresh={freshKeys} />
              )}
            </div>
          </div>
        </div>
      )}
      {paywall && (
        <PaywallModal
          reason={paywall.reason}
          message={paywall.message}
          resumeAdventureId={id}
          onClose={() => setPaywall(null)}
        />
      )}
    </div>
  );
}

function RecapCard({ turn }) {
  const [open, setOpen] = useState(true);
  const swipe = useSwipe({ onUp: () => setOpen(false), onDown: () => setOpen(true) });
  return (
    <div className="fade-up recap-scroll px-5 py-4 my-2" data-testid="turn-recap" data-turn-id={turn.turn_id} data-open={open ? "true" : "false"}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-3 group py-2 -my-2 touch-none select-none"
        data-testid="recap-toggle"
        aria-expanded={open}
        {...swipe}
      >
        <span className="font-mono text-[0.6rem] uppercase tracking-[0.4em] gold">
          ◆ Chapter {turn.chapter_closed} Recap
        </span>
        <span className="font-mono text-[0.6rem] uppercase tracking-widest text-stone-500 group-hover:text-amber-400 transition-colors">
          {open ? "Fold ▴" : "Unfurl ▾"}
        </span>
      </button>
      {open && (
        <div className="mt-3 font-prose italic text-stone-300 text-base leading-relaxed" data-testid="recap-text">
          {turn.narrative}
        </div>
      )}
      <div className="mt-3 text-center font-serif-display text-xs tracking-[0.35em] uppercase text-amber-500/80" data-testid="recap-next-chapter">
        Chapter {turn.chapter_opened} begins
      </div>
    </div>
  );
}

function TurnBlock({ turn, isFirst, animate, onRevealed }) {
  if (turn.role === "recap") return <RecapCard turn={turn} />;
  if (turn.role === "player") {
    return (
      <div className="fade-up border-l-2 border-amber-500/40 pl-4 py-1" data-testid="turn-player">
        <div className="font-mono text-[0.6rem] uppercase tracking-widest text-stone-500 mb-1">You</div>
        <div className="font-serif-body italic text-stone-400">{turn.narrative}</div>
      </div>
    );
  }

  return (
    <div className="fade-up space-y-2" data-testid="turn-gm" data-turn-id={turn.turn_id}>
      {turn.encounter && (
        <div className="inline-block px-3 py-1 border border-red-500/50 text-red-400 font-mono text-[0.6rem] uppercase tracking-widest rounded-sm">
          ⚡ Random Encounter
        </div>
      )}
      {turn.death && (
        <div className="inline-block px-3 py-1 border border-purple-500/50 text-purple-300 font-mono text-[0.6rem] uppercase tracking-widest rounded-sm">
          ✝ You have died
        </div>
      )}
      {animate ? (
        <TypewriterNarrative
          text={turn.narrative}
          isFirst={isFirst}
          turnId={turn.turn_id}
          onDone={onRevealed}
        />
      ) : (
        <StaticNarrative text={turn.narrative} isFirst={isFirst} />
      )}
    </div>
  );
}

function StaticNarrative({ text, isFirst }) {
  return (
    <div className={`font-prose text-stone-200 text-base sm:text-lg leading-relaxed prose-tome ${isFirst ? "dropcap" : ""}`}>
      {text.split(/\n\n+/).map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </div>
  );
}

function TypewriterNarrative({ text, isFirst, turnId, onDone }) {
  const [visibleCount, setVisibleCount] = useState(0);
  const [done, setDone] = useState(false);
  const words = React.useMemo(() => text.split(/(\s+)/), [text]); // keep whitespace
  const totalUnits = words.length;

  useEffect(() => {
    if (done) return;
    if (visibleCount >= totalUnits) {
      setDone(true);
      onDone?.(turnId);
      return;
    }
    // Slightly longer pause after punctuation for drama
    const current = words[visibleCount];
    const isPunctuationEnd = /[.!?…—]$/.test((current || "").trim());
    const isCommaEnd = /[,;:]$/.test((current || "").trim());
    const isWhitespace = /^\s+$/.test(current || "");
    const baseMs = isWhitespace ? 0 : 55;  // words fly in
    const extra = isPunctuationEnd ? 260 : isCommaEnd ? 120 : 0;
    const t = setTimeout(() => setVisibleCount((c) => c + 1), baseMs + extra);
    return () => clearTimeout(t);
  }, [visibleCount, totalUnits, words, done, onDone, turnId]);

  const skip = () => {
    if (done) return;
    setVisibleCount(totalUnits);
  };

  const rendered = words.slice(0, visibleCount).join("");
  const paragraphs = rendered.split(/\n\n+/);

  return (
    <div
      onClick={skip}
      data-testid="typewriter-narrative"
      data-turn-id={turnId}
      title={done ? "" : "Tap to reveal"}
      className={`font-prose text-stone-200 text-base sm:text-lg leading-relaxed prose-tome relative ${isFirst ? "dropcap" : ""} ${done ? "" : "cursor-pointer"}`}
    >
      {paragraphs.map((p, i) => (
        <p key={i}>
          {p}
          {i === paragraphs.length - 1 && !done && (
            <span className="inline-block w-[0.4em] h-[1em] align-[-0.15em] ml-0.5 bg-amber-500 animate-pulse" aria-hidden="true" />
          )}
        </p>
      ))}
      {!done && (
        <div className="absolute -bottom-6 right-0 font-mono text-[0.6rem] uppercase tracking-widest text-stone-600 select-none">
          Tap to reveal
        </div>
      )}
    </div>
  );
}
